import { OP_FULL_NAMES, OP_LEVELS_ASCENDING, type OpLevel } from "@/config/operatingPoints";
import { PRIORITY_RANK, type ClinicalTier } from "@/utils/findingPriority";
import type { SelectedAttribute } from "@/types/predictions";
import type { TriageLabeling } from "@/types/triageReview";

/**
 * A device panel: every device run on the same studies (web-demo-backend
 * `/api/device-panels`). Its `view` picks how it is shown: `research` reads the
 * devices side by side, with the panel's `research_label` on every surface,
 * cleared devices included; `notification` is the site worklist and a
 * notification page per study, as a site or a sales demo sees them.
 */

/** `pending`: job not finished; `error`: unreadable output or a mismatched job row. */
export type DevicePanelDeviceState = "completed" | "pending" | "failed" | "error" | "missing";

/** Text for a device with no readable result; its `message` (if any) goes in the title. */
export const STATE_TEXT: Record<Exclude<DevicePanelDeviceState, "completed">, string> = {
  pending: "Running",
  failed: "Run failed",
  error: "Output unreadable",
  missing: "No result",
};

/** `triage`: a strip row; `findings`: a findings list; `compare`: shown only
 * against the device it names in `compare_with`. */
export type DevicePanelRole = "triage" | "findings" | "compare";

export interface DevicePanelDevice {
  key: string;
  label: string;
  short: string;
  /** The device's regulatory status; null for a research view's triage
   * devices, which that view does not show. */
  status_label: string | null;
  role: DevicePanelRole;
  /** The device a `compare` device is read against; null for other roles. */
  compare_with: string | null;
  /** The notification wording of a device with indications (a cleared device);
   * null for any other device. */
  labeling: TriageLabeling | null;
}

/** The exam and age that routing read for a study; all null when no device
 * routes by indications or the study is not canonicalized. */
export interface DevicePanelExam {
  exam: string | null;
  age_years: number | null;
  age_recorded: boolean | null;
}

/** A panel's worklist route. */
export const panelUrl = (panelId: string) => `/panel/${encodeURIComponent(panelId)}`;

/** One study's route in a panel. */
export const panelCaseUrl = (panelId: string, studyUid: string) =>
  `${panelUrl(panelId)}/case/${encodeURIComponent(studyUid)}`;

export type PanelView = "research" | "notification";

/** Where a notification view's "Open study" goes: web-demo's own viewer (a demo
 * or the drill site) or the site's PACS (a live site). */
export type OpenStudyTarget = "viewer" | "pacs";

/** How the panel is shown; whether it admits only cleared devices (those with
 * indications); and the research label its surfaces show, required in the
 * research view (null only on a cleared-only panel). */
export interface PanelKind {
  view: PanelView;
  open_study: OpenStudyTarget;
  cleared_only: boolean;
  research_label: string | null;
}

/** One finding flagged at default by the study's in-scope triage devices. Devices'
 * findings are merged by the backend's configured groups: `key` and
 * `display_name` are the group's, or the condition and the first flagging
 * device's name when ungrouped. */
export interface DevicePanelTriageFinding {
  key: string;
  display_name: string;
  /** Most operating points cleared by a flagging device; null when none has a ladder. */
  ops_cleared: number | null;
  /** The flagging devices in panel order, each with its own name and reading. */
  devices: {
    key: string;
    display_name: string;
    ops_cleared: number | null;
    default_operating_point: OpLevel;
    allowed_operating_points: OpLevel[] | null;
    /** What this device's call means in a grouped finding, e.g. ">3 cm"; null
     * when the finding is not grouped. */
    qualifier: string | null;
  }[];
}

export interface DevicePanelStudyRow extends DevicePanelExam {
  study_uid: string;
  label: string;
  /** Exam region, e.g. `abd-pelvis`, `chest`. */
  region: string;
  /** Strongest first (most points cleared, then most devices). */
  triage_findings: DevicePanelTriageFinding[];
  /** The findings device's flags at default; null without a completed result. */
  findings_count: number | null;
  devices: {
    key: string;
    state: DevicePanelDeviceState;
    applicable: boolean;
    /** Why a device with indications was not applied; null otherwise. */
    not_applicable_reason: string | null;
    /** Null unless the device completed. */
    flagged_at_default_count: number | null;
  }[];
}

/** A panel the caller may open (`GET /api/device-panels`). */
export interface DevicePanelSummary {
  id: string;
  name: string;
  study_count: number;
}

export interface DevicePanelStudiesResponse {
  panel: PanelKind & {
    id: string;
    name: string;
    /** A device applies by its `indications` (its intended-use config) or by
     * exam `regions` (which may include `any`), never both. */
    devices: (DevicePanelDevice & { regions: string[] | null; indications: string | null })[];
  };
  studies: DevicePanelStudyRow[];
}

/** In-scope triage devices with no readable result on a listed study, e.g.
 * "Omni: running", so a missing finding is not read as a quiet device. */
export function unreadTriage(row: DevicePanelStudyRow, devices: readonly DevicePanelDevice[]) {
  return devices
    .filter((device) => device.role === "triage")
    .flatMap((device) => {
      const result = row.devices.find((entry) => entry.key === device.key);
      if (!result) throw new Error(`Study ${row.study_uid} has no entry for device ${device.key}`);
      return result.applicable && result.state !== "completed"
        ? [`${device.short}: ${STATE_TEXT[result.state].toLowerCase()}`]
        : [];
    });
}

/** Nested operating points: clearing one implies clearing every more sensitive one. */
export type OperatingPointCalls = Record<OpLevel, boolean>;

export interface DevicePanelFinding {
  condition: string;
  display_name: string;
  /** Null for a present/absent device (Unified-Triage). */
  operating_points: OperatingPointCalls | null;
  /** How many of the four points the finding clears; null with `operating_points`. */
  ops_cleared: number | null;
  /** The finding's own default point (set for present/absent devices too). */
  default_operating_point: OpLevel;
  flagged_at_default: boolean;
  /** Key slices as SOP Instance UIDs; the first is the jump target. */
  top_slices: string[];
  /** Drafter: selected `[{dim, value}]`; other devices may send a scored map. */
  attributes: SelectedAttribute[] | Record<string, unknown> | null;
  /** Clinical tier (`acute`, `prompt_workup`, …) where the device assigns one. */
  category: string | null;
  /** The panel's reviewed finding group joining this condition with other
   * devices' (always null for findings and compare devices), with what this
   * device's call means within it, e.g. "moderate+" (null is an error). */
  group: { key: string; display_name: string; qualifier: string | null } | null;
  /** Points, most sensitive first, at which the device allows a below-default
   * call to be raised; null when the device has no such list (Unified, findings
   * and compare devices). Absent from backends that predate it: a below-default
   * call then cannot be placed. */
  allowed_operating_points?: OpLevel[] | null;
}

// version, job_id and study_id are null when the device has no result to read.
export interface DevicePanelStudyDevice extends DevicePanelDevice {
  cohort_id: string;
  version: string | null;
  job_id: string | null;
  /** `uid::job` of the device's job. */
  study_id: string | null;
  state: DevicePanelDeviceState;
  /** Set when the device failed or its output could not be read. */
  message: string | null;
  /** False when the device is built for another region than the study's, or
   * the study is outside its indications. */
  applicable: boolean;
  /** Why a device with indications was not applied; null otherwise. */
  not_applicable_reason: string | null;
  /** Only findings positive at one or more operating points. Null without a
   * completed result, and for a completed not-applicable device on a panel that
   * withholds output (a partner's): it has no calls to show. */
  findings: DevicePanelFinding[] | null;
}

export interface DevicePanelStudyResponse extends PanelKind, DevicePanelExam {
  panel_id: string;
  name: string;
  study_uid: string;
  label: string;
  region: string;
  /** In display order. */
  devices: DevicePanelStudyDevice[];
}

/** Meter bars, most sensitive first. */
export const METER_LEVELS = OP_LEVELS_ASCENDING;

/** What a call is read from: a device's ladder for a finding, its own default,
 * and the points it allows below that default. */
export type Reading = Pick<
  DevicePanelFinding,
  | "operating_points"
  | "ops_cleared"
  | "default_operating_point"
  | "flagged_at_default"
  | "allowed_operating_points"
>;

/** A list entry's reading. The list carries only calls flagged at default, so
 * the per-point calls a below-default reading needs are not sent. */
export const listReading = (entry: DevicePanelTriageFinding["devices"][number]): Reading => ({
  ...entry,
  operating_points: null,
  flagged_at_default: true,
});

/** 1-based position of the reading's default operating point, most sensitive
 * first. A ladder without a valid default is a data-shape regression, never a
 * guessed default. */
export function defaultBar(reading: Reading): number {
  const bar = METER_LEVELS.indexOf(reading.default_operating_point) + 1;
  if (bar === 0)
    throw new Error(
      `Reading has operating points but no valid default_operating_point (${reading.default_operating_point})`,
    );
  return bar;
}

/** The reading in words, e.g. "Clears 2 of 4 · default: Sensitivity Biased";
 * null without a ladder. */
export function readingText(reading: Reading): string | null {
  return reading.ops_cleared === null
    ? null
    : `Clears ${reading.ops_cleared} of ${METER_LEVELS.length} · default: ${
        OP_FULL_NAMES[reading.default_operating_point]
      }`;
}

/**
 * How a call reads against its finding's own default point:
 * - flagged: clears the default (a present/absent call when present); `stricter`
 *   when it also clears a stricter point, a quiet cue rather than a word;
 * - consider: below the default but true at a point that is both allowed and
 *   below the default (checked per point, not from the count cleared);
 * - hidden: below the default and true at no allowed point, so never shown;
 * - below: below the default on a device with no allowed list (findings and
 *   compare devices): a dim row in their demoted list, never a triage call;
 * - incomplete: below the default with the allowed list not sent at all, so it
 *   cannot be placed (an error state for its row, never a guess).
 */
export type CallReading =
  | { kind: "flagged"; stricter: boolean }
  | { kind: "consider" }
  | { kind: "below" }
  | { kind: "hidden" }
  | { kind: "incomplete" };

export function readCall(reading: Reading): CallReading {
  if (reading.ops_cleared === null) {
    if (!reading.flagged_at_default) throw new Error("Reading has no ladder and is not present");
    return { kind: "flagged", stricter: false };
  }
  const atDefault = defaultBar(reading);
  if (reading.ops_cleared >= atDefault)
    return { kind: "flagged", stricter: reading.ops_cleared > atDefault };
  if (reading.allowed_operating_points === undefined) return { kind: "incomplete" };
  if (reading.allowed_operating_points === null) return { kind: "below" };
  const raised = reading.allowed_operating_points.some((level) => {
    const bar = METER_LEVELS.indexOf(level) + 1;
    if (bar === 0) throw new Error(`Unknown allowed operating point ${level}`);
    return bar < atDefault && reading.operating_points?.[level] === true;
  });
  return { kind: raised ? "consider" : "hidden" };
}

/** The two words a shown call reads as, each with its one-line meaning. */
export const WORDS = [
  { kind: "flagged", label: "Flagged", meaning: "Flagged at the device's default point" },
  {
    kind: "consider",
    label: "consider",
    meaning: "Below the default, at a point the device allows",
  },
] as const;

const CALL_RANK: Record<CallReading["kind"], number> = {
  flagged: 0,
  consider: 1,
  below: 2,
  incomplete: 3,
  hidden: 4,
};

/** Flagged, consider, below, incomplete; then most points cleared. */
export function byCall(a: Reading, b: Reading): number {
  return (
    CALL_RANK[readCall(a).kind] - CALL_RANK[readCall(b).kind] ||
    (b.ops_cleared ?? 0) - (a.ops_cleared ?? 0)
  );
}

/** One tooltip line for a device's call: its name for the finding and reading. */
export function callLine(short: string, displayName: string, reading: Reading): string {
  const text = readingText(reading);
  const incomplete = readCall(reading).kind === "incomplete" ? " · allowed points not sent" : "";
  return `${short}: ${displayName}${text ? ` · ${text}` : ""}${incomplete}`;
}

/** A device named in a grouped finding with what its call means there, e.g.
 * "Omni (moderate+)"; null when its qualifier is missing, an error to show. */
export const qualifiedName = (short: string, qualifier: string | null) =>
  qualifier === null ? null : `${short} (${qualifier})`;

/** A completed device's shown findings (hidden calls dropped), strongest first. */
export function calledFindings(device: DevicePanelStudyDevice): DevicePanelFinding[] {
  if (device.state !== "completed" || !device.findings)
    throw new Error(`Device ${device.key} has no completed findings`);
  return device.findings.filter((finding) => readCall(finding).kind !== "hidden").sort(byCall);
}

/** One device's call on one finding. */
export interface DeviceCall {
  device: DevicePanelStudyDevice;
  finding: DevicePanelFinding;
}

/** One finding as called by one or more devices: its name, each device's call
 * in panel order, and the strongest of those calls. */
export interface FindingCalls {
  key: string;
  display_name: string;
  calls: DeviceCall[];
  strongest: DeviceCall;
}

/**
 * The completed devices' calls, one entry per finding, strongest first. Calls
 * join as the backend joins them for the list: on the panel's reviewed finding
 * group, else on an identical condition key. A group takes its own name; an
 * ungrouped finding takes the first calling device's. A call below every allowed
 * point (or on a device with no allowed list) is left out unless `keep` holds
 * it, as for a call in the review queue.
 */
export function findingCalls(
  devices: readonly DevicePanelStudyDevice[],
  keep: (call: DeviceCall) => boolean,
): FindingCalls[] {
  const joined = new Map<string, { display_name: string; calls: DeviceCall[] }>();
  for (const device of devices)
    for (const finding of (device.findings ?? []).filter((called) => {
      const kind = readCall(called).kind;
      return (kind !== "hidden" && kind !== "below") || keep({ device, finding: called });
    })) {
      const key = finding.group?.key ?? finding.condition;
      const entry = joined.get(key) ?? {
        display_name: finding.group?.display_name ?? finding.display_name,
        calls: [],
      };
      entry.calls.push({ device, finding });
      joined.set(key, entry);
    }
  return [...joined]
    .map(([key, { display_name, calls }]) => ({
      key,
      display_name,
      calls,
      strongest: [...calls].sort((a, b) => byCall(a.finding, b.finding))[0],
    }))
    .sort((a, b) => byCall(a.strongest.finding, b.strongest.finding));
}

/** Whether a device's output is withheld here: completed, with no findings sent. */
const withheld = (device: DevicePanelStudyDevice) =>
  device.state === "completed" && device.findings === null;

/** The call a study opens on in View: the strongest call of its top triage row,
 * when that row reads Flagged; null when triage flags nothing here. */
export function firstFlagged(
  devices: readonly DevicePanelStudyDevice[],
): { device: string; condition: string } | null {
  const [top] = findingCalls(triageDevices(devices).inScope, () => false);
  return top && readCall(top.strongest.finding).kind === "flagged"
    ? { device: top.strongest.device.key, condition: top.strongest.finding.condition }
    : null;
}

/** The triage strip's devices split into in-scope and not-applicable. A
 * not-applicable device whose output the panel withholds has nothing to show,
 * so it is left out. */
export function triageDevices(devices: readonly DevicePanelStudyDevice[]) {
  const triage = devices.filter((device) => device.role === "triage");
  return {
    inScope: triage.filter((device) => device.applicable),
    notApplicable: triage.filter((device) => !device.applicable && !withheld(device)),
  };
}

/**
 * Whether the panel lists a device's finding as a row on its own, with no
 * review queue or link holding it: a triage call that reads Flagged, consider or
 * incomplete; any shown call of a findings device; a compared device's call
 * that is flagged at default or paired with the findings device's own.
 */
export function isListed(
  devices: readonly DevicePanelStudyDevice[],
  { device: key, condition }: { device: string; condition: string },
): boolean {
  const device = devices.find((candidate) => candidate.key === key);
  const finding = device?.findings?.find((candidate) => candidate.condition === condition);
  if (!device || !finding) return false;
  const kind = readCall(finding).kind;
  if (kind === "hidden") return false;
  if (device.role === "triage") return kind !== "below";
  if (device.role === "findings") return true;
  const own = devices.find((candidate) => candidate.key === device.compare_with);
  return (
    own?.state === "completed" &&
    (finding.flagged_at_default ||
      calledFindings(own).some((called) => called.condition === condition))
  );
}

/** A drafter finding's selected attribute values, e.g. `multiple · impacted`. */
export function attributeText(finding: DevicePanelFinding): string | null {
  const { attributes } = finding;
  if (attributes === null) return null;
  if (!Array.isArray(attributes))
    throw new Error(`Finding ${finding.condition} attributes must be [{dim, value}]`);
  return attributes.length ? attributes.map((attribute) => attribute.value).join(" · ") : null;
}

const tierRank = (category: string | null) =>
  category !== null && category in PRIORITY_RANK ? PRIORITY_RANK[category as ClinicalTier] : -1;

/** Items grouped by clinical tier, highest first, untiered last; item order kept. */
function byTier<T>(items: readonly T[], category: (item: T) => string | null) {
  const groups = new Map<string | null, T[]>();
  for (const item of items)
    groups.set(category(item), [...(groups.get(category(item)) ?? []), item]);
  return [...groups]
    .map(([tier, rows]) => ({ category: tier, rows }))
    .sort((a, b) => tierRank(b.category) - tierRank(a.category));
}

/** A findings device's findings grouped by clinical tier, strongest call first in each. */
export function findingGroups(device: DevicePanelStudyDevice) {
  return byTier(calledFindings(device), (finding) => finding.category);
}

/** One finding as read by a findings device and by the device compared against it. */
export interface ComparedFinding {
  condition: string;
  own: DevicePanelFinding | null;
  compared: DevicePanelFinding | null;
}

/** byCall, with an unreported finding last. */
const byCallReported = (a: DevicePanelFinding | null, b: DevicePanelFinding | null) =>
  a && b ? byCall(a, b) : Number(!a) - Number(!b);

/**
 * The findings device's findings paired by condition with the compared device's,
 * plus the compared device's findings that the findings device does not report
 * and that are flagged at default (or that `keep` holds, as queued for review). Grouped by tier; strongest own call first, then compared.
 */
export function comparedGroups(
  own: DevicePanelStudyDevice,
  compared: DevicePanelStudyDevice,
  keep: (finding: DevicePanelFinding) => boolean,
) {
  const theirs = new Map(calledFindings(compared).map((finding) => [finding.condition, finding]));
  const rows: ComparedFinding[] = calledFindings(own).map((finding) => ({
    condition: finding.condition,
    own: finding,
    compared: theirs.get(finding.condition) ?? null,
  }));
  const reported = new Set(rows.map((row) => row.condition));
  for (const finding of theirs.values())
    if (!reported.has(finding.condition) && (finding.flagged_at_default || keep(finding)))
      rows.push({ condition: finding.condition, own: null, compared: finding });
  rows.sort((a, b) => byCallReported(a.own, b.own) || byCallReported(a.compared, b.compared));
  return byTier(rows, (row) => (row.own ?? row.compared)!.category);
}

/** Each `findings` device with the `compare` device read against it, if any. */
export function findingsSections(devices: readonly DevicePanelStudyDevice[]) {
  return devices
    .filter((device) => device.role === "findings")
    .map((device) => ({
      device,
      compare:
        devices.find((other) => other.role === "compare" && other.compare_with === device.key) ??
        null,
    }));
}

/** Key slices the viewer ticks: every finding flagged at default by an in-scope
 * triage device or a findings device. Compared devices are left out. */
export function flaggedKeySlices(devices: readonly DevicePanelStudyDevice[]): string[] {
  const read = [
    ...triageDevices(devices).inScope,
    ...findingsSections(devices).map((section) => section.device),
  ];
  return [
    ...new Set(
      read
        .filter((device) => device.state === "completed")
        .flatMap((device) => calledFindings(device))
        .filter((finding) => finding.flagged_at_default && finding.top_slices.length > 0)
        .map((finding) => finding.top_slices[0]),
    ),
  ];
}
